import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import { Bot, Sparkles, ArrowRight, MessageSquare, Layers, Globe, FileText, Zap, ShieldAlert, Palette, Check, RotateCcw, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useStaffTheme, STAFF_THEME_DEFAULT, STAFF_THEME_PRESETS } from "@/hooks/use-staff-theme";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  getStaffPortalBlockedMessage,
  getStaffPortalReadiness,
} from "@/lib/staff-portal-readiness";

type PortalBot = {
  id: string;
  name: string;
  description: string | null;
  type: string;
  apiKey: string;
  primaryColor: string | null;
  botIcon: string | null;
  welcomeMessage: string | null;
};

function BotTypeIcon({ type }: { type: string }) {
  switch (type) {
    case "website":     return <Globe className="w-4 h-4" />;
    case "document":    return <FileText className="w-4 h-4" />;
    case "combo":       return <Layers className="w-4 h-4" />;
    case "ai-enhanced": return <Zap className="w-4 h-4" />;
    default:            return <Bot className="w-4 h-4" />;
  }
}

function PersonalizeDialog() {
  const { primary, setPrimary, reset, presets } = useStaffTheme();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(primary);

  const onApply = () => {
    if (/^#[0-9a-fA-F]{6}$/.test(custom)) setPrimary(custom);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setCustom(primary); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="button-personalize">
          <Palette className="w-3.5 h-3.5 mr-1.5" />
          Personalize
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="dialog-personalize">
        <DialogHeader>
          <DialogTitle>Choose your chat colour</DialogTitle>
          <DialogDescription>
            This styles the chat window only — when you open an assistant from this portal, the messages and bot avatar use the colour you pick. It's saved in this browser and doesn't affect anyone else in your organisation, or the portal itself.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-muted-foreground">Presets</Label>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
              {presets.map((p) => {
                const isActive = p.value.toLowerCase() === custom.toLowerCase();
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setCustom(p.value)}
                    className="a11y-tip group relative aspect-square rounded-lg border-2 hover-elevate active-elevate-2 flex items-center justify-center"
                    style={{ backgroundColor: p.value, borderColor: isActive ? "white" : "transparent", boxShadow: isActive ? `0 0 0 2px ${p.value}` : undefined }}
                    data-tip={p.name}
                    aria-label={p.name}
                    data-testid={`button-color-${p.value.replace("#", "").toLowerCase()}`}
                  >
                    {isActive && <Check className="w-4 h-4 text-white drop-shadow" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label htmlFor="custom-color" className="text-xs text-muted-foreground">Custom (hex)</Label>
            <div className="flex gap-2 mt-2">
              <input
                id="custom-color-swatch"
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(custom) ? custom : STAFF_THEME_DEFAULT}
                onChange={(e) => setCustom(e.target.value)}
                className="h-10 w-12 rounded border bg-background cursor-pointer"
                aria-label="Pick a custom colour"
                data-testid="input-color-swatch"
              />
              <Input
                id="custom-color"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="#1fa8e0"
                maxLength={7}
                className="font-mono uppercase"
                data-testid="input-color-hex"
              />
            </div>
          </div>

          <div className="rounded-lg border p-3">
            <p className="text-xs text-muted-foreground mb-2">Preview</p>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: custom }}>
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 px-3 py-2 rounded-md text-xs text-white" style={{ backgroundColor: custom }}>
                Hi! How can I help you today?
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => { reset(); setCustom(STAFF_THEME_DEFAULT); }}
            data-testid="button-color-reset"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            Reset
          </Button>
          <Button type="button" onClick={onApply} data-testid="button-color-apply">
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Neutral fallback used when a bot has no branded colour configured.
// We deliberately do NOT use the staff member's personal colour here:
// the colour picker is scoped to the chat window only, so the portal
// itself stays on the bot's branded look.
const PORTAL_FALLBACK_COLOR = STAFF_THEME_PRESETS[0].value;

export default function StaffPortal() {
  const [, setLocation] = useLocation();
  const { user, organization } = useAuth();
  const [checkingBotId, setCheckingBotId] = useState<string | null>(null);
  const [blockedBot, setBlockedBot] = useState<{ name: string; message: string } | null>(null);

  const openBot = async (bot: PortalBot) => {
    if (checkingBotId) return;
    setCheckingBotId(bot.id);
    try {
      const response = await fetch(`/api/v1/chatbots/${bot.apiKey}/config`, { cache: "no-store" });
      const config = response.ok ? await response.json() : null;
      if (getStaffPortalReadiness(config)?.isReady === true) {
        setLocation(`/chat/${bot.apiKey}?from=portal`);
        return;
      }
      setBlockedBot({ name: bot.name, message: getStaffPortalBlockedMessage(config) });
    } catch {
      setBlockedBot({ name: bot.name, message: getStaffPortalBlockedMessage(null) });
    } finally {
      setCheckingBotId(null);
    }
  };

  // staleTime:0 + refetchOnMount:"always" guarantees a fresh network call
  // every time the portal mounts (critical after plan provisioning, where
  // a pre-provisioning empty result must never be reused from cache).
  // isFetching tracks in-flight requests even when cached data exists, so
  // we never flash "No assistants" while a fetch is still in progress.
  const { data: bots, isLoading: botsLoading, isFetching } = useQuery<PortalBot[]>({
    queryKey: ["/api/me/chatbots"],
    staleTime: 0,
    refetchOnMount: "always",
  });

  // Auth may still be resolving after billing/provisioning completes.
  // Show loading whenever:
  //   • auth is still resolving
  //   • bots are loading for the first time (no data at all)
  //   • a fetch is in-flight AND the current data is empty — provisioning
  //     may have completed since the last (pre-provisioning) fetch so an
  //     empty cached result must not be treated as confirmed while it is
  //     being revalidated. Note: `!bots` alone is insufficient because
  //     `![]` === false; we must also check `.length === 0`.
  const { isLoading: authLoading } = useAuth();
  const isLoading = authLoading || botsLoading || (isFetching && (!bots || bots.length === 0));

  return (
    <div className="min-h-full bg-gradient-to-b from-background to-muted/30">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <Link href="/portal" data-testid="link-back-to-portal">
          <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6 cursor-pointer">
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to portal
          </span>
        </Link>
        <div className="flex items-start justify-between gap-4 mb-10">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1.5" data-testid="text-portal-org">
              <Sparkles className="w-3.5 h-3.5 text-muted-foreground" />
              <span>{organization?.name || "Your organisation"}</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight" data-testid="text-portal-greeting">
              Hi {user?.name?.split(" ")[0] || "there"} 👋
            </h1>
            <p className="text-muted-foreground mt-2 max-w-xl">
              Pick an assistant below to start a conversation. Your colleagues can't see what you ask.
            </p>
          </div>
          <PersonalizeDialog />
        </div>

        {isLoading ? (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="state-portal-loading">
            <Skeleton className="h-44" />
            <Skeleton className="h-44" />
          </div>
        ) : !bots || bots.length === 0 ? (
          <Card data-testid="state-portal-empty">
            <CardContent className="py-16 text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <ShieldAlert className="w-6 h-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold">No assistants are available yet</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                  Your organisation hasn't given you access to any chatbots, or the bots in your org are inactive. Reach out to your IT admin to get set up.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="grid-portal-bots">
            {bots.map((bot) => {
              // Bot cards use the bot's own branded colour (set by an
              // admin). The staff member's personal "Personalize"
              // colour is intentionally NOT applied here — it only
              // styles the chat window itself.
              const color = bot.primaryColor || PORTAL_FALLBACK_COLOR;
              return (
                <Card
                  key={bot.id}
                  className={`group overflow-hidden border-2 ${
                    checkingBotId ? "cursor-wait opacity-70" : "hover-elevate active-elevate-2 cursor-pointer"
                  } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
                  role="button"
                  tabIndex={checkingBotId ? -1 : 0}
                  aria-disabled={checkingBotId !== null}
                  aria-busy={checkingBotId === bot.id}
                  onClick={() => void openBot(bot)}
                  onKeyDown={(event) => {
                    if (checkingBotId || (event.key !== "Enter" && event.key !== " ")) return;
                    event.preventDefault();
                    void openBot(bot);
                  }}
                  data-testid={`card-bot-${bot.id}`}
                >
                  <div className="h-1.5" style={{ backgroundColor: color }} />
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white"
                          style={{ backgroundColor: color }}
                        >
                          <Bot className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <CardTitle className="text-base truncate" data-testid={`text-bot-name-${bot.id}`}>
                            {bot.name}
                          </CardTitle>
                          <Badge variant="secondary" className="mt-1 text-[10px] gap-1">
                            <BotTypeIcon type={bot.type} />
                            {bot.type.replace(/-/g, " ")}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CardDescription className="line-clamp-2 min-h-[2.5rem]" data-testid={`text-bot-desc-${bot.id}`}>
                      {bot.description || bot.welcomeMessage || "Ask questions, get answers, anytime."}
                    </CardDescription>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <MessageSquare className="w-3.5 h-3.5" />
                        {checkingBotId === bot.id ? "Checking setup…" : "Start a conversation"}
                      </span>
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-0.5 group-hover:text-foreground transition-all" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <Dialog open={blockedBot !== null} onOpenChange={(open) => { if (!open) setBlockedBot(null); }}>
            <DialogContent className="sm:max-w-md" data-testid="dialog-staff-portal-readiness">
              <DialogHeader>
                <DialogTitle>{blockedBot?.name} is not ready yet</DialogTitle>
                <DialogDescription className="whitespace-pre-line text-left">
                  {blockedBot?.message}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button onClick={() => setBlockedBot(null)} data-testid="button-close-readiness">
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </>
        )}
      </div>
    </div>
  );
}
